import React, {useCallback, useMemo} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMemoCircleInfo,
} from '@fortawesome/pro-regular-svg-icons'
import DataTableSection from 'src/components/era-package/forms/DataTableSection'
import {
  query,
  selectDoc,
  airManifest,
  oceanManifest,
  railManifest,
  truckManifest,
  inBond,
  importerBond,
  manufacturerMID,
  optionsMap as headerOptions, responseQuery
} from 'src/views/abi/fields/abi-query-list'
import {
  addTabKeyToFields,
  addTabKeyToOptions
} from 'src/utils/era-package/tab-utils'
import {createColumnHelper} from "@tanstack/react-table";
import {Box} from "@mui/material";
import AbiUserSettingsApiUrls from "../../../../apiUrls/AbiUserSettingsApiUrls";
import AbiQueryApiUrls from "../../../../apiUrls/AbiQueryApiUrls";
import DataTableSectionWithUpload from "../../../../components/era-package/forms/DataTableSectionWithUpload";
import AbiEntrySummaryApiUrls from "../../../../apiUrls/AbiEntrySummaryApiUrls";
import QueryListTable from './QueryListTable';

/**
 * Each hook should return an `Object` the following:
 * @param {String} name - tab identifier
 * @param {Array} formSections - Form sections and fields to display.
 *                  FIELD NAMES SHOULD FOLLOW `tabName.fieldName`
 * @param {Array} defaultValues - Default values to be fed into the form manager.
 *                  FIELD NAMES SHOULD FOLLOW `fieldName`
 * @param {Object} optionsMap - Options to use in the form
 *                  FIELD NAMES SHOULD FOLLOW `tabName.fieldName`
 * @param {Array?} field - OPTIONAL Flat array of all fields. This is used to generate the validation schema.
 *                  FIELD NAMES SHOULD FOLLOW `fieldName`
 * @param {(Object) => Yup} schema - OPTIONAL Validation schema. Takes in an object and expects a Yup object in return.
 */

export const useQueryListForm = (fetchedData, setFetchedData) => {
  const key = 'queryList'

  const fetchResponse = async (row) => {
    try {
      const id = row?.queryId
      const response = await fetch(`${AbiQueryApiUrls.GET_RESPONSE + `?id=${id}`}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      })

      if (!response.ok) {
        console.log('Error')
      }

      const json = await response.json()
      setFetchedData({
        ...fetchedData,
        response: { ...json.data, queryType: row?.queryType }
      })
      return json.data
    } catch (error) {
      console.log('Error')
    }
  }

    // Set up form sections including table sections
  const formSections = [
    {
      title: 'Query List',
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />,
      type: 'table',
      name: `${key}.queryList`,
      tableComponent: QueryListTable,
      tableFields: query,
      enableUpload: false,
      handleCustomEdit: fetchResponse,
      disableAdd: true,
      enableDelete: false,
    }
  ]

  return {
    name: key,
    formSections,
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useSelectDocForm = (id) => {
  const key = 'selectDoc'

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[...selectDoc].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Select Document Type',
      fields: addTabKeyToFields(selectDoc, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useAirManifestForm = (id) => {
  const key = 'airManifest'

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[...airManifest].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Air Manifest',
      fields: addTabKeyToFields(airManifest, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useOceanManifestForm = (id) => {
  const key = 'oceanManifest'

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[...oceanManifest].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Ocean Manifest',
      fields: addTabKeyToFields(oceanManifest, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useRailManifestForm = (id) => {
  const key = 'railManifest'

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[...railManifest].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Rail Manifest',
      fields: addTabKeyToFields(railManifest, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useTruckManifestForm = (id) => {
  const key = 'truckManifest'

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[...truckManifest].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Truck Manifest',
      fields: addTabKeyToFields(truckManifest, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useInBondForm = (id) => {
  const key = 'inBond'

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[...inBond].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'In-Bond',
      fields: addTabKeyToFields(inBond, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useImporterBondForm = (id) => {
  const key = 'importerBond'

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[...importerBond].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Importer Bond',
      fields: addTabKeyToFields(importerBond, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useManufacturerMIDForm = (id) => {
  const key = 'manufacturerMID'

  const defaultValues = () => {
    const initialData = {}

      // Initialize regular fields
    ;[...manufacturerMID].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Manufacturer(MID)',
      fields: addTabKeyToFields(manufacturerMID, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections]
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true
  }
}

export const useResponseForm = (id) => {
  const key = 'response'

  const defaultValues = () => {
    const initialData = {}

      // Initialize regular fields
    ;[...responseQuery].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Response',
      fields: addTabKeyToFields(responseQuery, key),
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        return section.fields
      }),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: addTabKeyToFields(allFields, key),
    optionsMap: addTabKeyToOptions(headerOptions, key),
    hideSave: true,
    readOnly: true,
    displayRefresh: true,
  }
}
